import { useEffect, useState } from "react";

import type { AdminCategorySyncSettings } from "../../api/client";

export type CategorySyncApi = {
  loadCategorySync: () => Promise<AdminCategorySyncSettings>;
  configureCategorySync: (automatic: boolean) => Promise<AdminCategorySyncSettings>;
  runCategorySync: (requestId: string) => Promise<{
    replayed: boolean;
    run: NonNullable<AdminCategorySyncSettings["currentRun"]>;
  }>;
};

const activeStates = new Set(["pending", "claimed", "waiting"]);

const runLabel = (settings: AdminCategorySyncSettings | null) => {
  const run = settings?.currentRun;
  if (!run) return "Синхронизация ещё не запускалась";
  if (run.state === "pending" || run.state === "claimed") return "Обновляем исходный прайс…";
  if (run.state === "waiting") return `Обновляем посты: ${run.succeededCount + run.failedCount} из ${run.queuedCount}`;
  if (run.state === "failed") return `Синхронизация завершилась с ошибкой${run.failedCount ? `: ${run.failedCount} пост(а)` : ""}`;
  return `Готово: обновлено ${run.succeededCount || run.queuedCount} пост(а)`;
};

const nextRunLabel = (value: string | null) => {
  if (!value) return "Автосинхронизация выключена";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Следующий запуск запланирован";
  return `Следующий запуск: ${new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date)}`;
};

export function AdminCategorySyncScreen({
  api,
  onClose
}: {
  api: CategorySyncApi;
  onClose: () => void;
}) {
  const [settings, setSettings] = useState<AdminCategorySyncSettings | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    const next = await api.loadCategorySync();
    setSettings(next);
    return next;
  };

  useEffect(() => {
    let active = true;
    api.loadCategorySync().then(
      (value) => { if (active) setSettings(value); },
      () => { if (active) setError("Не удалось загрузить настройки синхронизации."); }
    );
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!settings?.currentRun || !activeStates.has(settings.currentRun.state)) return;
    const timer = window.setTimeout(() => {
      void refresh().catch(() => setError("Не удалось обновить статус синхронизации."));
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [settings]);

  const configure = async (automatic: boolean) => {
    setPending(true);
    setError(null);
    try {
      setSettings(await api.configureCategorySync(automatic));
    } catch {
      setError("Не удалось сохранить настройку. Попробуйте ещё раз.");
    } finally {
      setPending(false);
    }
  };

  const run = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await api.runCategorySync(crypto.randomUUID());
      setSettings((current) => current ? { ...current, currentRun: result.run } : current);
    } catch {
      setError("Не удалось запустить синхронизацию. Попробуйте ещё раз.");
    } finally {
      setPending(false);
    }
  };

  const running = Boolean(settings?.currentRun && activeStates.has(settings.currentRun.state));
  return <section className="screen-stack section-screen">
    <header className="composer-header">
      <button type="button" aria-label="Назад в раздел Ещё" onClick={onClose}>←</button>
      <div><p className="eyebrow">Только админ</p><h1>Синхронизация категорий</h1></div>
      <span />
    </header>
    {error && <p className="inline-error" role="alert">{error}</p>}
    <section className="category-sync-card">
      <div>
        <strong>Категории и посты прайса</strong>
        <p>Обновляет исходный прайс, главный пост-каталог и только связанные автообновляемые посты THLS.</p>
      </div>
      <label className="category-sync-toggle">
        <span><strong>Автоматически каждый час</strong><small>{nextRunLabel(settings?.nextRunAt ?? null)}</small></span>
        <input
          type="checkbox"
          aria-label="Автоматически каждый час"
          checked={settings?.automatic ?? false}
          disabled={!settings || pending}
          onChange={(event) => void configure(event.currentTarget.checked)}
        />
      </label>
    </section>
    <section className={`category-sync-status ${settings?.currentRun?.state === "failed" ? "failed" : ""}`} aria-live="polite">
      <strong>{runLabel(settings)}</strong>
      <small>Клиентские посты и личные настройки пользователей не изменяются.</small>
    </section>
    <button className="primary-button" type="button" disabled={!settings || pending || running} onClick={() => void run()}>
      {running ? "Синхронизация выполняется…" : "Синхронизировать сейчас"}
    </button>
  </section>;
}
